import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AlertCircle, CheckCircle2, Loader2, MailCheck, RotateCcw } from "lucide-react";
import { memberApi } from "../api/member";
import { pageRoutes } from "../utils";

export default function MemberVerifyEmail() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [verificationToken, setVerificationToken] = useState("");
  const [code, setCode] = useState("");
  const [status, setStatus] = useState("idle");
  const [resendStatus, setResendStatus] = useState("idle");
  const [cooldown, setCooldown] = useState(0);
  const [message, setMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    setEmail(sessionStorage.getItem("pendingVerificationEmail") || "");
    setVerificationToken(
      sessionStorage.getItem("pendingVerificationToken") || ""
    );
    const pendingMessage = sessionStorage.getItem("pendingVerificationMessage");
    if (pendingMessage) {
      setMessage(pendingMessage);
      sessionStorage.removeItem("pendingVerificationMessage");
    }
  }, []);

  useEffect(() => {
    if (cooldown <= 0) {
      return undefined;
    }
    const timer = window.setTimeout(() => setCooldown((value) => value - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [cooldown]);

  function updateCode(value) {
    setCode(value.replace(/\D/g, "").slice(0, 4));
  }

  async function verifyCode(event) {
    event.preventDefault();
    if (code.length !== 4 || status === "loading") {
      return;
    }

    setStatus("loading");
    setErrorMessage("");
    setMessage("");

    try {
      const payload = await memberApi("/api/auth/verify-email-code", {
        method: "POST",
        body: JSON.stringify({ verificationToken, email, code }),
      });
      sessionStorage.removeItem("pendingVerificationEmail");
      sessionStorage.removeItem("pendingVerificationToken");
      setStatus("success");
      setMessage(payload.message || "Your email has been verified.");
      if (payload.nextStep === "stripe_checkout" && payload.checkoutUrl) {
        window.location.assign(payload.checkoutUrl);
        return;
      }
      window.setTimeout(() => navigate(pageRoutes.MemberLogin, { replace: true }), 900);
    } catch (error) {
      setStatus("error");
      setErrorMessage(
        error instanceof Error
          ? error.message
          : "Invalid or expired verification code."
      );
    }
  }

  async function resendCode() {
    if (!email || cooldown > 0 || resendStatus === "loading") {
      return;
    }
    setResendStatus("loading");
    setErrorMessage("");
    try {
      await memberApi("/api/auth/resend-email-code", {
        method: "POST",
        body: JSON.stringify({ email }),
      });
      setCooldown(60);
      setResendStatus("idle");
      setMessage("If this account needs verification, a new code has been sent.");
    } catch (error) {
      setResendStatus("error");
      setErrorMessage(
        error instanceof Error ? error.message : "Unable to resend the code."
      );
    }
  }

  return (
    <div className="min-h-screen bg-[#FDF8F3] px-4 py-16">
      <div className="mx-auto max-w-md rounded-3xl border border-[#C9A227]/20 bg-white p-8 shadow-sm">
        <div className="flex items-center gap-3">
          <MailCheck className="h-7 w-7 text-[#722F37]" />
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.22em] text-[#A88520]">
              Email Verification
            </p>
            <h1 className="font-serif-elegant text-3xl font-bold text-[#722F37]">
              Enter your 4-digit code
            </h1>
          </div>
        </div>

        <p className="mt-5 text-sm text-gray-600">
          We sent a 4-digit verification code to your email. Please enter it to
          verify your account.
        </p>
        {email && (
          <p className="mt-2 text-sm font-semibold text-gray-800">
            Code sent to: {email}
          </p>
        )}

        {message && (
          <div className="mt-6 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
            <div className="flex gap-3">
              <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <span>{message}</span>
            </div>
          </div>
        )}

        {errorMessage && (
          <div className="mt-6 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900">
            <div className="flex gap-3">
              <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <span>{errorMessage}</span>
            </div>
          </div>
        )}

        <form onSubmit={verifyCode} className="mt-8 space-y-5">
          <label className="block">
            <span className="mb-2 block text-sm font-medium text-gray-700">
              4-digit code
            </span>
            <input
              inputMode="numeric"
              pattern="[0-9]*"
              value={code}
              onChange={(event) => updateCode(event.target.value)}
              className="w-full rounded-xl border border-gray-300 px-4 py-3 text-center text-2xl font-bold tracking-[0.35em] focus:outline-none focus:ring-2 focus:ring-[#C9A227]"
              maxLength="4"
              autoComplete="one-time-code"
            />
          </label>

          <button
            type="submit"
            disabled={code.length !== 4 || status === "loading" || (!verificationToken && !email)}
            className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#722F37] px-5 py-3 font-semibold text-white hover:bg-[#5A252C] disabled:opacity-60"
          >
            {status === "loading" ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <MailCheck className="h-4 w-4" />
            )}
            Verify email
          </button>
        </form>

        <div className="mt-5 flex flex-col gap-3 sm:flex-row">
          <button
            type="button"
            onClick={resendCode}
            disabled={!email || cooldown > 0 || resendStatus === "loading"}
            className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl border border-[#722F37]/30 px-4 py-2 text-sm font-semibold text-[#722F37] hover:bg-[#722F37]/10 disabled:opacity-60"
          >
            {resendStatus === "loading" ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RotateCcw className="h-4 w-4" />
            )}
            {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend code"}
          </button>
          <Link
            to={pageRoutes.MemberLogin}
            className="flex-1 rounded-xl border border-gray-200 px-4 py-2 text-center text-sm font-semibold text-gray-700 hover:bg-gray-50"
          >
            Back to login
          </Link>
        </div>
      </div>
    </div>
  );
}
